<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6" v-if="isFetched && mapPanelDataFetched">
      <card-title-row :straightTitle="$i18n.t('sidebar.locationLab.reports')" class="pt-5 pb-2">
        <template #buttons>
          <base-rounded-button
            v-if="isEnlargmentVisible"
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.createReport')"
            @click="toggleReportDialog"
          />
        </template>
      </card-title-row>
      <sidebar-row class="mb-2">
        <v-checkbox v-model="isDrawToolActive" hide-details>
          <template #label>
            <v-row style="color: #202124 !important" class="text-body-2" no-gutters>
              {{ $i18n.t('sidebar.generateReportToDrawnArea') }}
            </v-row>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row v-if="isDrawToolActive">
        <v-row class="mt-2" no-gutters>
          <v-col cols="auto">
            <dots-menu :items="dotsItems" @multiple="turnOnDrawing('multiple')" @freehand="turnOnDrawing('freehand')">
              <template #activator="{ on }">
                <base-button
                  v-on="getButtonListeners(on)"
                  :disabled="isDrawn"
                  :button-style="drawButtonStyle"
                  translation-path="sidebar.drawArea"
                />
              </template>
            </dots-menu>
          </v-col>
          <v-col v-if="isDrawn" cols="auto">
            <base-button translation-path="sidebar.clear" cancel @click="clearSelection" />
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row flex-wrap="flex-nowrap" slot-cols="11" class="pt-2 pb-3">
        <location-lab-reports-list
          ref="location-lab-reports-list"
          :reports="reports"
          :calculating="calculating"
          :enlargment="isEnlargmentVisible"
          @toggleReportGenerate="toggleReportGenerate"
        />
      </sidebar-row>
      <template v-if="calculating">
        <main-loader class="pt-10" />
      </template>
      <template v-else>
        <sidebar-row flex-wrap="flex-nowrap" slot-cols="11" border-top v-if="currentReport">
          <v-col cols="12" class="pa-0 pt-6">
            <v-row no-gutters align="center" class="pb-2">
              <v-col cols="auto">
                <v-row no-gutters style="font-size: 20px" class="location-lab-report-name">
                  {{ currentReport.name }}
                </v-row>
                <v-row
                  no-gutters
                  class="location-lab-report-features"
                  v-if="!currentReport.has_multiple_layers_statistics"
                  >{{
                    $i18n.t('dialog.numberOfObjectsFrom', {
                      number:
                        currentReport.calculatedStats[0].features_count ||
                        currentReport.calculatedStats[0].total_features_count,
                      from: currentReport.calculatedStats[0].total_features_count,
                    })
                  }}
                </v-row>
              </v-col>
            </v-row>
            <v-row v-for="(stat, idx) of currentReport.calculatedStats" :key="idx" class="pt-2" no-gutters>
              <v-col cols="12">
                <v-data-table
                  style="border: 1px solid rgba(0, 0, 0, 0.12)"
                  v-bind="dataTableProps"
                  :headers="getHeaders(stat.headers)"
                  :items="getItems(stat)"
                  item-key="ordinalNumber"
                >
                  <template #top>
                    <v-toolbar
                      style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)"
                      color="rgba(207, 207, 207, 0.25)"
                      dense
                      flat
                    >
                      <v-toolbar-title style="font-size: 16px" class="font-weight-medium">
                        {{ stat.statistics_name }}
                      </v-toolbar-title>
                      <v-spacer />
                      <v-tooltip bottom color="font">
                        <template #activator="{ on }">
                          <v-btn v-on="on" v-bind="buttonTableProps" @click="openCompleteReport(stat)">
                            <v-icon>mdi-magnify</v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{ $i18n.t('button.displayCompleteReport') }}
                        </span>
                      </v-tooltip>
                      <v-tooltip bottom color="font">
                        <template #activator="{ on }">
                          <v-btn v-on="on" v-bind="buttonTableProps" @click="getXlsx(stat)">
                            <v-icon>mdi-microsoft-excel</v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{ $i18n.t('button.exportReportToXlsx') }}
                        </span>
                      </v-tooltip>
                    </v-toolbar>
                  </template>
                  <template #body.append v-if="stat.chartData">
                    <tr>
                      <td class="px-0 py-4 chart_hover" :colspan="stat.headers.length + 1">
                        <chart-bar
                          v-bind="stat.chartData"
                          cols="12"
                          :name="`location-lab-chart-${idx}`"
                          :ref="`location-lab-chart-${idx}`"
                        />
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
        </sidebar-row>
      </template>
      <main-large-dialog
        v-if="isCompleteReportLoaded"
        :is-visible.sync="isCompleteReportVisible"
        :title="currentReportXlsxPayload.statistics_name"
        agree-text="dialog.exportExcel"
        disagree-text="dialog.close"
        :loading="loading"
        dialog-width="50%"
        no-paddings-y
        no-paddings-x
        header-shadow
        @agree="getXlsx(currentReportXlsxPayload)"
      >
        <template #body>
          <v-col cols="12">
            <v-data-table
              v-bind="dataTableProps"
              :headers="getHeaders(currentReportXlsxPayload.headers)"
              :items="itemsCompleteReport"
              item-key="ordinalNumber"
            />
          </v-col>
        </template>
      </main-large-dialog>
    </v-col>
    <main-loader v-else class="pt-10" />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { createBox } from 'ol/interaction/Draw';
import CardTitleRow from '@/components/CardTitleRow';
import ChartBar from '@/components/ChartBar';
import LocationLabReportsList from '@/components/LocationLabReportsList';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import DotsMenu from '@/components/DotsMenu';
export default {
  name: 'LocationLabReports',
  components: {
    CardTitleRow,
    ChartBar,
    LocationLabReportsList,
    MainLoader,
    SidebarRow,
    DotsMenu,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    currentLayerId: get('layers/currentLayer@id'),
    currentUser: get('users/currentUser'),
    reports: get('locationLab/reports'),
    selectedFeatures: get('map/selectedFeatures'),
    isEnlargmentEnable: get('admin/settingsValues@location_lab_edit_enabled'),
    isEnlargmentVisible() {
      return this.isEnlargmentEnable || this.currentUser?.is_superadmin;
    },
    drawButtonStyle() {
      return this.isDrawing
        ? {
            backgroundColor: `${this.$_colors.primarybackground} !important`,
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
  },
  watch: {
    isDrawToolActive(nV) {
      if (!nV) {
        this.clearSelection();
      }
    },
  },
  data: () => ({
    calculating: false,
    currentReport: undefined,
    currentReportXlsxPayload: undefined,
    isDialogVisible: false,
    isDialogLoaded: false,
    isFetched: false,
    newReport: { name: '', statistics: [], regions: {} },
    isDrawToolActive: false,
    isDrawing: false,
    isDrawn: false,
    drawnArea: undefined,
    dotsItems: [
      {
        name: 'multiple',
      },
      {
        name: 'freehand',
      },
    ],
    dataTableProps: {
      footerProps: {
        itemsPerPageOptions: [10, 20, 50],
      },
      calculateWidths: true,
      disableSort: true,
    },
    buttonTableProps: {
      icon: true,
      color: 'font',
      width: 30,
      height: 30,
      ripple: false,
    },
    itemsCompleteReport: [],
    isCompleteReportLoaded: false,
    isCompleteReportVisible: false,
    loading: false,
  }),
  methods: {
    calculateLocationLabReport: call('locationLab/calculateLocationLabReport'),
    getReports: call('locationLab/getLocationLabReports'),
    getLocationLabReportXlsx: call('locationLab/getLocationLabReportXlsx'),
    async init() {
      this.isFetched = false;
      try {
        await this.getReports();
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
    async getXlsx(stat) {
      try {
        this.loading = true;
        const payload = {
          report_name: this.currentReport.name,
          ...stat,
        };
        delete payload.chartData;
        delete payload.features_count;
        delete payload.total_features_count;
        await this.getLocationLabReportXlsx(payload);
      } finally {
        this.loading = false;
      }
    },
    async toggleReportGenerate(report) {
      this.calculating = true;
      try {
        const data = {
          id: report.id,
          payload: {
            ids: [],
            extent: this.drawnArea,
          },
        };
        if (!report.has_multiple_layers_statistics) {
          const layer = report.statistics[0].layer;
          if (this.currentLayerId && this.currentLayerId == layer) {
            const selectedFeatures = this.selectedFeatures[layer];
            data.payload.ids = selectedFeatures || [];
          }
        }
        const r = await this.calculateLocationLabReport(data);
        this.currentReport = JSON.parse(JSON.stringify(report));
        this.currentReport.calculatedStats = r.statistics.map(calculatedStat => {
          const statisticMetadata = this.currentReport.statistics.find(
            report => report.name === calculatedStat.statistics_name
          );
          if (!statisticMetadata.with_chart || !statisticMetadata.aggregate_attribute_name) {
            return calculatedStat;
          }
          const { aggregate_attribute_name, attribute_name } = statisticMetadata;
          calculatedStat.items.sort((a, b) => b[attribute_name] - a[attribute_name]);
          const chartData = {
            data: calculatedStat.items.map(item => item[attribute_name]),
            labels: calculatedStat.items.map(item => item[aggregate_attribute_name]),
            horizontal: statisticMetadata.chart_type === 'bar_horizontal',
          };
          return { ...calculatedStat, chartData };
        });
      } finally {
        this.calculating = false;
      }
    },
    toggleReportDialog() {
      this.$refs?.['location-lab-reports-list']?.toggleEdit(this.newReport);
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    getButtonListeners(on) {
      return this.isDrawing
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    turnOnDrawing(type) {
      this.isDrawing = true;
      this.$root.$emit('drawSidebarGeometry', {
        drawendCallback: this.onDrawend,
        geometryFunction: type === 'multiple' ? createBox() : null,
        geometryType: 'polygon',
        isInitSnapping: false,
        freehand: type === 'freehand' ? true : false,
        ...(type === 'multiple' ? { drawType: 'Circle' } : {}),
      });
    },
    turnOffSelecting() {
      this.isDrawing = false;
      this.drawnArea = undefined;
      this.$root.$emit('selection-action');
    },
    onDrawend(e) {
      this.isDrawing = false;
      this.isDrawn = true;
      this.drawnArea = {
        type: 'FeatureCollection',
        features: [
          {
            type: e.feature.getGeometry().getType(),
            properties: {},
            id: 1,
            geometry: {
              type: 'Polygon',
              coordinates: e.feature.getGeometry().getCoordinates(),
              crs: {
                type: 'name',
                properties: {
                  name: this.$_config.defaultEpsg || 'EPSG:4326',
                },
              },
            },
          },
        ],
      };
    },
    clearSelection() {
      this.$root.$emit('clearSidebarGeometry');
      this.turnOffSelecting();
      this.isDrawn = false;
    },
    getHeaders(headers) {
      const tableHeaders = headers.map(header => {
        return {
          text: header.attribute_verbose_name,
          value: header.attribute_name,
          align: 'center',
        };
      });
      return [
        {
          text: this.$i18n.t('sidebar.locationLab.ordinalNumber'),
          value: 'ordinalNumber',
          align: 'center',
        },
        ...tableHeaders,
      ];
    },
    getItems(stat) {
      const { aggregate_attribute_name, attribute_name } = this.currentReport?.statistics?.find(
        statistic => statistic.name === stat.statistics_name
      );
      return stat.items
        .sort((a, b) => b[attribute_name] - a[attribute_name])
        .map((item, idx) => {
          if (item[aggregate_attribute_name] === null) {
            item[aggregate_attribute_name] = 'NULL';
          }
          return { ordinalNumber: idx + 1, ...item };
        });
    },
    openCompleteReport(stat) {
      this.currentReportXlsxPayload = JSON.parse(JSON.stringify(stat));
      this.itemsCompleteReport = this.getItems(this.currentReportXlsxPayload);
      this.isCompleteReportLoaded = true;
      this.isCompleteReportVisible = true;
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.clearSelection();
  },
};
</script>
<style lang="scss" scoped>
.border {
  @include border(all, 1px);
}
.border-bottom {
  @include border(bottom, 2px);
}
.location-lab-report-name {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: #202124;
}
.location-lab-report-features {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.59);
  margin-top: 4px !important;
}
::v-deep {
  .v-data-table-header th,
  .v-data-table__wrapper td {
    white-space: nowrap;
  }
}
.chart_hover:hover {
  background-color: #fff !important;
}
</style>
